import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { tv } from 'tailwind-variants';

import Text, {
  type ResponsiveTextToken,
  type TextToken,
} from '../base/TextNew';
import CheckedIcon from '../icons/CheckedIcon';
import RadioFilledIcon from '../icons/RadioFilledIcon';
import RadioUnfilledIcon from '../icons/RadioUnfilledIcon';
import UncheckedIcon from '../icons/UncheckedIcon';

type CheckboxTextToken = TextToken | ResponsiveTextToken;
type CheckboxColor = 'default' | 'settings';

const checkbox = tv({
  base: 'cursor-pointer',
  variants: {
    disabled: {
      true: 'bg-gray-30 cursor-not-allowed opacity-50',
      false: 'cursor-pointer',
    },
  },
});

export default function Checkbox({
  id,
  checked,
  className,
  align = 'end',
  label,
  labelSecondary,
  labelToken = '14/medium',
  labelSecondaryToken,
  labelClassName,
  labelSecondaryClassName,
  textClassName,
  setChecked,
  variant = 'radio',
  color = 'default',
  disabled,
}: {
  id: string;
  checked: boolean;
  className?: string;
  align?: 'start' | 'center' | 'end';
  label?: string;
  labelSecondary?: string;
  labelToken?: CheckboxTextToken;
  labelSecondaryToken?: CheckboxTextToken;
  labelClassName?: string;
  labelSecondaryClassName?: string;
  textClassName?: string;
  setChecked: (checked: boolean) => void;
  variant?: 'radio' | 'checkbox';
  color?: CheckboxColor;
  disabled?: boolean;
}) {
  const labelToneClassName = textClassName
    ? ''
    : color === 'settings'
      ? 'text-gray-60 dark:text-gray-30'
      : 'text-primary';
  const controlToneClassName = textClassName
    ? ''
    : color === 'settings'
      ? 'text-blue data-[state=unchecked]:text-gray-60'
      : 'text-gray-90 dark:text-gray-30';
  const labelStateClassName = disabled
    ? 'cursor-not-allowed opacity-50'
    : 'cursor-pointer';
  const secondaryToken = labelSecondaryToken ?? labelToken;
  const alignmentClassName =
    align === 'center'
      ? 'items-center'
      : align === 'start'
        ? 'items-start'
        : 'items-end';

  return (
    <div
      className={`flex gap-1.5 ${alignmentClassName} ${textClassName ?? ''} ${className ?? ''}`}
    >
      <RadixCheckbox.Root
        className={`${checkbox({ disabled })} ${controlToneClassName}`}
        id={id}
        checked={checked}
        onCheckedChange={setChecked}
        disabled={disabled}
      >
        {checked === true ? (
          <RadixCheckbox.Indicator>
            {variant === 'checkbox' ? <CheckedIcon /> : <RadioFilledIcon />}
          </RadixCheckbox.Indicator>
        ) : (
          <>
            {variant === 'checkbox' ? <UncheckedIcon /> : <RadioUnfilledIcon />}
          </>
        )}
      </RadixCheckbox.Root>
      <Text
        as="label"
        token={labelToken}
        className={`${labelToneClassName} ${labelStateClassName} ${labelClassName ?? ''}`}
        htmlFor={id}
      >
        {label}
      </Text>
      {labelSecondary && (
        <Text
          as="label"
          token={secondaryToken}
          className={`text-gray-60 dark:text-gray-30 ${labelStateClassName} ${labelSecondaryClassName ?? ''}`}
          htmlFor={id}
        >
          {labelSecondary}
        </Text>
      )}
    </div>
  );
}
